import { Link } from 'react-router-dom';

interface VideoItem {
  id: number;
  title: string;
  category: string;
  platform: string;
  image: string;
  rating: number;
  views?: string;
  quality?: string;
  updateTime?: string;
  episode?: string;
}

interface VideoListProps {
  title: string;
  subtitle?: string;
  icon?: string;
  videos: VideoItem[];
  showPlatform?: boolean;
  bgClass?: string;
}

export default function VideoList({
  title,
  subtitle,
  icon,
  videos,
  showPlatform = true,
  bgClass = 'bg-background-50',
}: VideoListProps) {
  return (
    <section className={`py-12 md:py-16 px-4 md:px-6 ${bgClass}`}>
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-3 mb-8">
          {icon && (
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600">
              <i className={`${icon} text-lg`}></i>
            </div>
          )}
          <div>
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-900">{title}</h2>
            {subtitle && <p className="text-sm text-foreground-500 mt-1">{subtitle}</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {videos.map((video) => (
            <Link
              key={video.id}
              to={`/article/${video.id}`}
              className="group block bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
            >
              <div className="aspect-video overflow-hidden relative">
                <img
                  src={video.image}
                  alt={video.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-3 left-3 flex items-center gap-2">
                  {video.quality && (
                    <span className="text-xs px-2 py-0.5 bg-black/60 text-white rounded-md backdrop-blur-sm">
                      {video.quality}
                    </span>
                  )}
                </div>
                <div className="absolute top-3 right-3">
                  <span className="text-xs px-2 py-0.5 bg-accent-500 text-white rounded-md font-medium">
                    {video.rating}
                  </span>
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-white/90 text-primary-500">
                    <i className="ri-play-fill text-2xl"></i>
                  </div>
                </div>
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-foreground-900 text-sm md:text-base group-hover:text-primary-600 transition-colors duration-200 line-clamp-1">
                  {video.title}
                </h3>
                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                  {showPlatform && (
                    <span className="text-xs px-2 py-0.5 bg-secondary-100 text-secondary-700 rounded-md">
                      {video.platform}
                    </span>
                  )}
                  <span className="text-xs text-foreground-400">{video.category}</span>
                  {video.episode && (
                    <span className="text-xs text-primary-500 font-medium ml-auto">{video.episode}</span>
                  )}
                  {video.updateTime && (
                    <span className="text-xs text-foreground-400 ml-auto">{video.updateTime}</span>
                  )}
                </div>
                {video.views && (
                  <p className="text-xs text-foreground-400 mt-1.5">
                    <i className="ri-eye-line mr-1"></i>
                    {video.views} 次观看
                  </p>
                )}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}